import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="analytics--activity-feed"
export default class extends Controller {
  static targets = ["feed", "item", "loading", "error", "empty", "loadMore"];
  static values = {
    refreshInterval: Number,
    feedUrl: String,
    maxItems: Number,
    currentPage: Number,
    hasMore: Boolean,
  };

  connect() {
    this.currentPageValue = 1;
    this.hasMoreValue = true;
    this.initializeFeed();
    this.setupEventListeners();
    this.startAutoRefresh();
  }

  disconnect() {
    this.cleanup();
  }

  // Initialize activity feed
  initializeFeed() {
    this.loadInitialFeed();
  }

  // Set up event listeners
  setupEventListeners() {
    this.element.addEventListener(
      "analytics:sectionDataUpdated",
      this.handleDataUpdate.bind(this),
    );
    this.element.addEventListener(
      "analytics:newActivity",
      this.handleNewActivity.bind(this),
    );

    // Set up intersection observer for infinite scroll
    this.setupInfiniteScroll();
  }

  // Clean up timers and event listeners
  cleanup() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    this.element.removeEventListener(
      "analytics:sectionDataUpdated",
      this.handleDataUpdate.bind(this),
    );
    this.element.removeEventListener(
      "analytics:newActivity",
      this.handleNewActivity.bind(this),
    );
  }

  // Start auto-refresh timer
  startAutoRefresh() {
    if (this.refreshIntervalValue && this.refreshIntervalValue > 0) {
      this.refreshTimer = setInterval(() => {
        this.refreshFeed();
      }, this.refreshIntervalValue * 1000); // Convert to milliseconds
    }
  }

  // Load initial feed data
  async loadInitialFeed() {
    if (!this.hasFeedUrlValue) {
      console.warn("No feed URL provided");
      this.showEmpty("No activity feed URL configured");
      return;
    }

    try {
      this.showLoading();
      this.hideError();

      const response = await this.fetchFeedData(1);
      const data = response.activities || [];

      this.clearFeed();
      this.renderActivities(data);

      this.hasMoreValue = response.has_more || false;
      this.updateLoadMoreButton();

      if (data.length === 0) {
        this.showEmpty("No recent activity");
      }
    } catch (error) {
      console.error("Failed to load activity feed:", error);
      this.showError("Failed to load activity feed");
    } finally {
      this.hideLoading();
    }
  }

  // Refresh activity feed data
  async refreshFeed() {
    if (!this.hasFeedUrlValue) return;

    try {
      const response = await this.fetchFeedData(1);
      const newActivities = response.activities || [];

      // Compare with existing activities to find new ones
      const existingIds = this.getExistingActivityIds();
      const freshActivities = newActivities.filter(
        (activity) => !existingIds.includes(activity.id),
      );

      // Add new activities to the top
      if (freshActivities.length > 0) {
        this.prependActivities(freshActivities);
        this.trimExcessItems();
      }

      // Update existing activities if needed
      this.updateExistingActivities(newActivities);
    } catch (error) {
      console.error("Failed to refresh activity feed:", error);
    }
  }

  // Load more activities (pagination)
  async loadMore() {
    if (!this.hasMoreValue || !this.hasFeedUrlValue) return;

    try {
      this.showLoadMoreLoading();

      const nextPage = this.currentPageValue + 1;
      const response = await this.fetchFeedData(nextPage);
      const newActivities = response.activities || [];

      if (newActivities.length > 0) {
        this.appendActivities(newActivities);
        this.currentPageValue = nextPage;
      }

      this.hasMoreValue = response.has_more || false;
      this.updateLoadMoreButton();
    } catch (error) {
      console.error("Failed to load more activities:", error);
      this.showError("Failed to load more activities");
    } finally {
      this.hideLoadMoreLoading();
    }
  }

  // Fetch feed data from server
  async fetchFeedData(page = 1) {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: (this.maxItemsValue || 20).toString(),
    });

    const response = await fetch(`${this.feedUrlValue}?${params}`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": this.getCSRFToken(),
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  }

  // Handle data updates from dashboard controller
  handleDataUpdate(event) {
    const { sectionType, data } = event.detail;

    if (sectionType === "activity" && data) {
      this.updateFeedFromData(data);
    }
  }

  // Handle new activity events
  handleNewActivity(event) {
    const { activity } = event.detail;
    if (activity) {
      this.addActivity(activity);
    }
  }

  // Update feed from section data
  updateFeedFromData(data) {
    if (data.recent_items) {
      this.clearFeed();
      this.renderActivities(data.recent_items);
    }
  }

  // Add new activity to feed
  addActivity(activity) {
    const activityElement = this.createActivityElement(activity);

    if (this.hasFeedTarget) {
      // Add to top with animation
      activityElement.style.opacity = "0";
      activityElement.style.transform = "translateY(-20px)";

      this.feedTarget.insertBefore(activityElement, this.feedTarget.firstChild);

      // Animate in
      requestAnimationFrame(() => {
        activityElement.style.transition = "all 0.3s ease-out";
        activityElement.style.opacity = "1";
        activityElement.style.transform = "translateY(0)";
      });

      // Trim excess items
      this.trimExcessItems();
    }
  }

  // Render activities in the feed
  renderActivities(activities) {
    if (!this.hasFeedTarget) return;

    activities.forEach((activity) => {
      const activityElement = this.createActivityElement(activity);
      this.feedTarget.appendChild(activityElement);
    });
  }

  // Prepend new activities to the top
  prependActivities(activities) {
    if (!this.hasFeedTarget) return;

    activities.reverse().forEach((activity) => {
      const activityElement = this.createActivityElement(activity);
      this.feedTarget.insertBefore(activityElement, this.feedTarget.firstChild);
    });
  }

  // Append activities to the bottom
  appendActivities(activities) {
    if (!this.hasFeedTarget) return;

    activities.forEach((activity) => {
      const activityElement = this.createActivityElement(activity);
      this.feedTarget.appendChild(activityElement);
    });
  }

  // Create activity element from data
  createActivityElement(activity) {
    const element = document.createElement("div");
    element.className =
      "activity-item border-b border-gray-100 py-3 hover:bg-gray-50 transition-colors";
    element.dataset.activityId = activity.id;
    element.dataset.analyticsActivityFeedTarget = "item";
    element.dataset.action = "click->analytics--activity-feed#handleItemClick";

    const timeAgo = this.formatTimeAgo(activity.created_at);
    const icon = this.getActivityIcon(activity.type);
    const urgentClass = activity.urgent ? "border-l-4 border-red-500 pl-3" : "";

    element.innerHTML = `
      <div class="flex items-start gap-3 ${urgentClass}">
        <div class="shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
          ${icon}
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm text-gray-900 ${activity.urgent ? "font-semibold" : ""}">
                ${activity.message}
              </p>
              ${
                activity.party_title
                  ? `
                <p class="text-xs text-gray-500 mt-1">
                  ${activity.party_title}
                </p>
              `
                  : ""
              }
            </div>
            <div class="shrink-0 ml-2">
              <span class="text-xs text-gray-500">${timeAgo}</span>
              ${activity.urgent ? '<span class="ml-2 text-xs text-red-600 font-medium">URGENT</span>' : ""}
            </div>
          </div>
          ${
            activity.action_url
              ? `
            <div class="mt-2">
              <a href="${activity.action_url}" class="text-xs text-blue-600 hover:text-blue-800 hover:underline">
                ${activity.action_text || "View Details"}
              </a>
            </div>
          `
              : ""
          }
        </div>
      </div>
    `;

    return element;
  }

  // Handle activity item click
  handleItemClick(event) {
    const activityItem = event.currentTarget;
    const activityId = activityItem.dataset.activityId;
    const actionUrl = activityItem.querySelector("a")?.href;

    if (actionUrl && !event.target.matches("a")) {
      // Navigate to action URL if not clicking on the link directly
      window.location.href = actionUrl;
    }

    // Mark as read
    this.markActivityAsRead(activityId);

    // Dispatch click event
    const clickEvent = new CustomEvent("analytics:activityClicked", {
      detail: { activityId, actionUrl },
      bubbles: true,
    });
    this.element.dispatchEvent(clickEvent);
  }

  // Mark activity as read
  markActivityAsRead(activityId) {
    const activityElement = this.element.querySelector(
      `[data-activity-id="${activityId}"]`,
    );
    if (activityElement) {
      activityElement.classList.add("opacity-75");
      activityElement.classList.remove("font-semibold");
    }
  }

  // Get existing activity IDs
  getExistingActivityIds() {
    return Array.from(this.itemTargets).map((item) => item.dataset.activityId);
  }

  // Update existing activities with new data
  updateExistingActivities(newActivities) {
    newActivities.forEach((activity) => {
      const existingElement = this.element.querySelector(
        `[data-activity-id="${activity.id}"]`,
      );
      if (existingElement) {
        // Update the element with new data if needed
        const newElement = this.createActivityElement(activity);
        existingElement.replaceWith(newElement);
      }
    });
  }

  // Trim excess items to maintain performance
  trimExcessItems() {
    if (!this.maxItemsValue) return;

    const items = this.itemTargets;
    if (items.length > this.maxItemsValue) {
      const excessItems = items.slice(this.maxItemsValue);
      excessItems.forEach((item) => item.remove());
    }
  }

  // Clear the feed
  clearFeed() {
    if (this.hasFeedTarget) {
      this.feedTarget.innerHTML = "";
    }
  }

  // Set up infinite scroll
  setupInfiniteScroll() {
    if (!this.hasLoadMoreTarget) return;

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && this.hasMoreValue) {
            this.loadMore();
          }
        });
      },
      {
        rootMargin: "100px",
      },
    );

    this.intersectionObserver.observe(this.loadMoreTarget);
  }

  // Update load more button visibility
  updateLoadMoreButton() {
    if (this.hasLoadMoreTarget) {
      this.loadMoreTarget.style.display = this.hasMoreValue ? "block" : "none";
    }
  }

  // Show loading state
  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden");
    }
  }

  // Hide loading state
  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden");
    }
  }

  // Show load more loading
  showLoadMoreLoading() {
    if (this.hasLoadMoreTarget) {
      const button = this.loadMoreTarget.querySelector("button");
      if (button) {
        button.disabled = true;
        button.textContent = "Loading...";
      }
    }
  }

  // Hide load more loading
  hideLoadMoreLoading() {
    if (this.hasLoadMoreTarget) {
      const button = this.loadMoreTarget.querySelector("button");
      if (button) {
        button.disabled = false;
        button.textContent = "Load More";
      }
    }
  }

  // Show error state
  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message;
      this.errorTarget.classList.remove("hidden");
    }
  }

  // Hide error state
  hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add("hidden");
    }
  }

  // Show empty state
  showEmpty(message) {
    if (this.hasEmptyTarget) {
      this.emptyTarget.textContent = message;
      this.emptyTarget.classList.remove("hidden");
    }
  }

  // Hide empty state
  hideEmpty() {
    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.add("hidden");
    }
  }

  // Get activity icon based on type
  getActivityIcon(type) {
    const icons = {
      party_created: "🎉",
      guest_rsvp: "👥",
      task_completed: "✅",
      task_overdue: "⚠️",
      budget_updated: "💰",
      vendor_booked: "🏢",
      timeline_updated: "📅",
      product_purchased: "🛍️",
      collaborator_added: "👤",
      default: "📝",
    };

    return icons[type] || icons.default;
  }

  // Format time ago string
  formatTimeAgo(timestamp) {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffInSeconds = Math.floor((now - activityTime) / 1000);

    if (diffInSeconds < 60) {
      return "Just now";
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h ago`;
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days}d ago`;
    } else {
      return activityTime.toLocaleDateString();
    }
  }

  // Get CSRF token for AJAX requests
  getCSRFToken() {
    const token = document.querySelector('meta[name="csrf-token"]');
    return token ? token.getAttribute("content") : "";
  }

  // Manual refresh action
  refresh() {
    this.refreshFeed();
  }

  // Pause auto-refresh
  pauseAutoRefresh() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  // Resume auto-refresh
  resumeAutoRefresh() {
    if (!this.refreshTimer && this.refreshIntervalValue > 0) {
      this.startAutoRefresh();
    }
  }

  // Toggle auto-refresh
  toggleAutoRefresh() {
    if (this.refreshTimer) {
      this.pauseAutoRefresh();
    } else {
      this.resumeAutoRefresh();
    }
  }
}
